import React, { useState } from 'react';

import Catalog from 'files/Catalog.js';
import Icon from 'views/site/Icon.js';
import Modal from 'views/site/Modal.js';
import SiteButton from 'views/site/Button.js';
import Theme from 'styles/Theme.js';
import Utils from 'files/Utils.js';

const ProductManuals = ({ style, type, utils }) => {

    const showPhotos = Catalog.isVisible(utils?.dealership?.get?.());

    /*
     * The manuals, as a list that opens a sheet.
     *
     * It used to be a single panel carrying a product dropdown, a version
     * dropdown and a download button — so choosing a manual meant operating
     * three controls before anything happened, and the page could only ever
     * show you one product at a time.
     *
     * Now the six manuals are a list, matching the article list directly above
     * them, and picking one opens a sheet to choose the revision and download.
     * The version choice belongs in the sheet because only three of the six
     * have more than one revision — putting it on the page meant a permanent
     * control that was inert half the time.
     */

    // The manual filenames on the server use their own keys, which predate the
    // catalogue and do not match its handles.
    const MANUAL_HANDLES = {
        bed_shaker: 'bed-shaker',
        carbon_monoxide: 'co-sensor',
        comm_link: 'comm-link',
        heat: 'heat-sensor',
        smoke: 'smoke-sensor',
        water: 'water-sensor'
    };

    const [hovered, setHovered] = useState(null);
    const [selected, setSelected] = useState(null);
    const [version, setVersion] = useState(1);

    const supportsMultipleVersions = key => {
        return ['carbon_monoxide', 'heat', 'smoke'].includes(key);
    }

    const getCatalogProduct = key => {
        return Catalog.getByHandle(MANUAL_HANDLES[key], 'full');
    }

    const getProductTitle = key => {
        let product = getCatalogProduct(key);
        if(product) {
            return product.title;
        }
        // fall back to the filename if the catalogue ever loses a handle
        return key.replace('_', ' ').split(' ').map(Utils.ucFirst).join(' ');
    }

    // Ordered by the catalogue, so this reads like the navigation, the products
    // page and the walkthrough results.
    const getProductItems = () => {
        let keys = Object.keys(MANUAL_HANDLES);
        return Catalog.getAll('full').reduce((list, product) => {
            let key = keys.find(item => MANUAL_HANDLES[item] === product.handle);
            if(key) {
                list.push({ id: key, product: product, title: product.title });
            }
            return list;
        }, []);
    }

    const getVersionItems = key => {
        return supportsMultipleVersions(key) ? [1, 2] : [1];
    }

    const getURL = (key, revision) => {
        return `https://${window.location.hostname}/troubleshooting/product_manuals/${key}.v${revision}.pdf`;
    }

    const onOpen = item => {
        setSelected(item.id);
        setVersion(supportsMultipleVersions(item.id) ? 2 : 1);
    }

    const onDownloadClick = () => {

        let url = getURL(selected, version);
        let link = document.createElement('a');
        link.style.display = 'none';
        link.href = url;
        link.target = '_blank';
        link.download = url.split('product_manuals/')[1];

        document.body.appendChild(link);
        link.click();

        // firefox needs a moment before the node can be removed
        setTimeout(() => {
            link.parentNode.removeChild(link);
        }, 0);
    }

    /*
     * The revision picker, as a select.
     *
     * It was a pair of pill buttons and they did not read as a choice: two
     * rounded shapes side by side in a dialog whose only other rounded shape is
     * the download button, so the selected one looked like a second action
     * rather than a state. A select says "pick one of these" without having to
     * be learned, and it is the control every browser already gives a keyboard
     * and a screen reader for free.
     */
    const getVersionPicker = () => {

        let versions = getVersionItems(selected);
        if(versions.length < 2) {
            return null;
        }

        let latest = Math.max(...versions);

        return (
            <div style={{ marginBottom: 24 }}>
                <label
                htmlFor={'manual-revision'}
                style={{
                    ...Theme.textStyles.caption({ color: Theme.colors.subText(), fontWeight: 600 }),
                    display: 'block',
                    marginBottom: 8
                }}>{'Which revision?'}</label>

                <select
                id={'manual-revision'}
                onChange={evt => setVersion(parseInt(evt.target.value, 10))}
                style={{
                    ...Theme.styles.input(),
                    backgroundColor: Theme.colors.white,
                    borderRadius: 12,
                    cursor: 'pointer',
                    height: 48,
                    maxWidth: 320,
                    padding: '0 16px'
                }}
                value={version}>
                    {versions.slice().reverse().map(item => (
                        <option key={item} value={item}>
                            {`Version ${item}.0${item === latest ? ' (latest)' : ''}`}
                        </option>
                    ))}
                </select>

                <span style={{
                    ...Theme.textStyles.caption({ color: Theme.colors.lightGrey }),
                    display: 'block',
                    marginTop: 10
                }}>{'The revision is printed on the label on the back of the device.'}</span>
            </div>
        )
    }

    return (
        <div style={{ width: '100%', ...style }}>
            <div style={{
                backgroundColor: Theme.colors.white,
                border: `1px solid ${Theme.colors.border()}`,
                borderRadius: 16,
                overflow: 'hidden',
                width: '100%'
            }}>
                {getProductItems().map((item, index) => (
                    <button
                    key={item.id}
                    onClick={onOpen.bind(this, item)}
                    onMouseEnter={setHovered.bind(this, item.id)}
                    onMouseLeave={setHovered.bind(this, null)}
                    style={{
                        alignItems: 'center',
                        backgroundColor: hovered === item.id ? Theme.colors.offWhite : 'transparent',
                        border: 'none',
                        borderTop: index === 0 ? 'none' : `1px solid ${Theme.colors.divider()}`,
                        cursor: 'pointer',
                        display: 'flex',
                        gap: 16,
                        justifyContent: 'space-between',
                        padding: Utils.isMobile() ? '14px 16px' : '16px 20px',
                        textAlign: 'left',
                        transition: 'background-color 0.15s ease',
                        width: '100%'
                    }}>
                        <div style={{
                            alignItems: 'center',
                            display: 'flex',
                            gap: 14,
                            minWidth: 0
                        }}>
                            {/* no device photos for a dealership that hides
                                the products - see Catalog.isVisible */}
                            {showPhotos && item.product.images?.[0] && (
                                <div style={{
                                    backgroundColor: Theme.colors.offWhite,
                                    borderRadius: 10,
                                    flexShrink: 0,
                                    height: 48,
                                    overflow: 'hidden',
                                    width: 48
                                }}>
                                    <img
                                    alt={''}
                                    src={item.product.images[0].src}
                                    style={{ height: '100%', objectFit: 'cover', width: '100%' }} />
                                </div>
                            )}
                            <div style={{ minWidth: 0 }}>
                                <span style={{
                                    ...Theme.textStyles.subHeading({ fontSize: Utils.isMobile() ? 16 : 17 }),
                                    display: 'block',
                                    marginBottom: 3
                                }}>{item.title}</span>
                                <span style={Theme.textStyles.caption({ color: Theme.colors.subText() })}>
                                    {supportsMultipleVersions(item.id) ? 'PDF · two revisions' : 'PDF'}
                                </span>
                            </div>
                        </div>
                        <Icon color={Theme.colors.primary()} name={'arrow-right'} size={14} />
                    </button>
                ))}
            </div>

            <Modal
            eyebrow={'Product manual'}
            onClose={setSelected.bind(this, null)}
            open={selected !== null}
            subtitle={selected ? `Component diagrams, placement guidance, and step-by-step instructions for installing, testing and cleaning the ${getProductTitle(selected)}.` : null}
            title={selected ? getProductTitle(selected) : ''}
            width={560}>
                {selected && (
                    <>
                        {getVersionPicker()}
                        <SiteButton
                        icon={'arrow-right'}
                        label={`Download the manual`}
                        onClick={onDownloadClick}
                        style={{ width: '100%' }}
                        type={'primary'} />
                    </>
                )}
            </Modal>
        </div>
    );
}

export default ProductManuals;
